<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <section class="w-full max-w-xl rounded-[32px] border border-gray-100 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
        <RouterLink class="back-home-link" to="/">
          <i class="ri-arrow-left-line"></i>
          <span>Back to home</span>
        </RouterLink>

        <h2 class="text-2xl font-semibold text-ink sm:text-[2rem]">Log in</h2>
        <p class="mt-2 text-sm leading-7 text-muted sm:text-base">{{ helperCopy }}</p>

        <AuthRoleTabs
          class="mt-6"
          :active-role="activeRole"
          @select="setRole"
        />

        <div v-if="activeRole === 'user'" class="mt-6 space-y-3">
          <button
            type="button"
            :disabled="googleLoading || loading"
            class="google-btn"
            @click="handleGoogleLogin"
          >
            <span class="google-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.9-5.4 3.9-3.2 0-5.8-2.7-5.8-6s2.6-6 5.8-6c1.8 0 3 .8 3.7 1.4l2.5-2.5C16.6 3.4 14.5 2.5 12 2.5 6.8 2.5 2.5 6.8 2.5 12S6.8 21.5 12 21.5c6.3 0 9.4-4.4 9.4-6.7 0-.4 0-.8-.1-1.1H12z"/>
                <path fill="#4285F4" d="M21.4 12.1c0-.4 0-.8-.1-1.1H12v3.9h5.4c-.3 1.4-1.1 2.5-2.3 3.3l3 2.3c1.8-1.7 3.3-4.2 3.3-7.8z"/>
                <path fill="#FBBC05" d="M6.2 14.3c-.2-.6-.4-1.3-.4-2s.1-1.4.4-2L3.1 8C2.7 8.9 2.5 10 2.5 11.2s.3 2.3.7 3.2l3-2.1z"/>
                <path fill="#34A853" d="M12 21.5c2.5 0 4.6-.8 6.2-2.3l-3-2.3c-.8.6-1.9 1-3.2 1-2.5 0-4.7-1.7-5.4-4l-3.1 2.4c1.6 3.1 4.8 5.2 8.5 5.2z"/>
              </svg>
            </span>
            <span>{{ googleLoading ? 'Connecting Google...' : 'Continue with Google' }}</span>
          </button>
          <div class="divider-row">
            <span>or continue with Username</span>
          </div>
        </div>

        <form class="mt-8 space-y-5" @submit.prevent="handleSubmit">
          <p v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
            {{ error }}
          </p>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass(focusedField === 'username', username)">
              <span class="field-floating-label">{{ usernameLabel }}</span>
              <input
                v-model.trim="username"
                type="text"
                required
                class="field-control"
                :inputmode="activeRole === 'vendor' ? 'email' : 'text'"
                :autocomplete="activeRole === 'vendor' ? 'username' : 'username'"
                @focus="focusedField = 'username'"
                @blur="focusedField = ''"
              >
            </div>
          </label>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass(focusedField === 'password', password)">
              <span class="field-floating-label">Password*</span>
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="field-control pr-14"
                @focus="focusedField = 'password'"
                @blur="focusedField = ''"
              >
              <button
                type="button"
                class="action-btn single-action"
                @click="showPassword = !showPassword"
                aria-label="Toggle password visibility"
              >
                <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
              </button>
            </div>
          </label>

          <div class="flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
            <label class="inline-flex items-center gap-2">
              <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand">
              <span>Keep me signed in</span>
            </label>
            <RouterLink class="font-semibold text-brand" :to="forgotPasswordLink">Forgot password?</RouterLink>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
          >
            {{ loading ? 'Signing in...' : submitLabel }}
          </button>
        </form>

        <div class="mt-8 text-center text-xs text-muted">
          New to Invi8?
          <RouterLink class="font-semibold text-brand" :to="registerLink">Create an account</RouterLink>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import AuthRoleTabs from '@/components/user/AuthRoleTabs.vue'
import { auth } from '@/services/firebase'
import userApi from '@/services/user/api'
import vendorApi from '@/services/vendor/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const loading = ref(false)
const googleLoading = ref(false)
const showPassword = ref(false)
const error = ref('')
const focusedField = ref('')
const activeRole = ref(route.query.role === 'vendor' ? 'vendor' : 'user')

const resetForm = () => {
  username.value = ''
  password.value = ''
  error.value = ''
  showPassword.value = false
  focusedField.value = ''
}

watch(
  () => route.query.role,
  (role) => {
    const nextRole = role === 'vendor' ? 'vendor' : 'user'
    if (nextRole !== activeRole.value) {
      resetForm()
    }
    activeRole.value = nextRole
  }
)

const helperCopy = computed(() =>
  activeRole.value === 'vendor'
    ? 'Use your vendor credentials to continue.'
    : 'Use your Invi8 account details to continue.'
)

const usernameLabel = computed(() =>
  activeRole.value === 'vendor' ? 'Email / Phone No*' : 'Username*'
)

const submitLabel = computed(() =>
  activeRole.value === 'vendor' ? 'Continue to dashboard' : 'Continue'
)

const registerLink = computed(() =>
  activeRole.value === 'vendor' ? '/vendor-signup?role=vendor' : '/register?role=user'
)

const forgotPasswordLink = computed(() => `/forgot-password?role=${activeRole.value}`)

const fieldShellClass = (focused, value) => ({
  'field-shell-active': focused || Boolean(value)
})

const setRole = (role) => {
  if (role === activeRole.value) return
  resetForm()
  router.replace({
    name: 'login',
    query: {
      ...route.query,
      role
    }
  })
}

const resolveUserId = (user) =>
  user?.s_id ||
  user?.id ||
  user?.user_id ||
  user?.vendor_id ||
  user?.vendorId ||
  ''

const getDefaultRedirect = (role) => (role === 'vendor' ? '/dashboard' : '/host')

const normalizeUser = (data, fallbackName) => {
  const responseItems = Array.isArray(data?.data) ? data.data : []
  const primaryUser = responseItems[0] || null
  const responseData = data?.data && typeof data.data === 'object' ? data.data : null
  return (
    data?.user ||
    primaryUser ||
    data?.data?.user ||
    data?.data?.profile ||
    data?.profile ||
    responseData ||
    (fallbackName
      ? {
          name: fallbackName,
          username: fallbackName
        }
      : null)
  )
}

const handleSubmit = async () => {
  error.value = ''
  loading.value = true

  const formData = new FormData()
  if (activeRole.value === 'vendor') {
    formData.append('s_username', username.value)
    formData.append('s_password', password.value)
  } else {
    formData.append('username', username.value)
    formData.append('password', password.value)
  }

  try {
    const { data } =
      activeRole.value === 'vendor'
        ? await vendorApi.loginVendor(formData)
        : await userApi.login(formData)

    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Login failed')
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const user = normalizeUser(data, username.value)
    const resolvedUserId = resolveUserId(user)

    if (token) {
      setAuthToken(token)
    }

    if (user) {
      setAuthUser({
        ...user,
        s_id: resolvedUserId || user?.s_id || '',
        role: activeRole.value,
        type: activeRole.value
      })
    }

    setAuthSession({
      loggedIn: true,
      role: activeRole.value,
      type: activeRole.value,
      s_id: resolvedUserId
    })

    const defaultRedirect = getDefaultRedirect(activeRole.value)
    const redirectPath = route.query.redirect || defaultRedirect
    router.push(redirectPath)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Login failed'
  } finally {
    loading.value = false
  }
}

const handleGoogleLogin = async () => {
  if (activeRole.value !== 'user') return
  googleLoading.value = true
  error.value = ''

  try {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    const googleUser = result.user
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    const offsetMinutes = new Date().getTimezoneOffset()
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
    const offsetMins = Math.abs(offsetMinutes) % 60
    const sign = offsetMinutes <= 0 ? '+' : '-'
    const offsetzone = `GMT${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`

    const formData = new FormData()
    formData.append('name', googleUser.displayName || '')
    formData.append('email', googleUser.email || '')
    formData.append('timezone', timezone)
    formData.append('offsetzone', offsetzone)
    formData.append('login_type', 'google')

    const { data } = await userApi.socialLogin(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Google login failed')
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const user = normalizeUser(data, googleUser.displayName || googleUser.email || 'Google User')
    const resolvedUserId = resolveUserId(user)

    if (token) {
      setAuthToken(token)
    }

    setAuthUser({
      ...user,
      email: user?.email || googleUser.email || '',
      s_id: resolvedUserId || user?.s_id || '',
      role: 'user',
      type: 'user'
    })
    setAuthSession({
      loggedIn: true,
      role: 'user',
      type: 'user',
      s_id: resolvedUserId
    })

    const redirectPath = route.query.redirect || getDefaultRedirect('user')
    router.push(redirectPath)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Google login failed'
  } finally {
    googleLoading.value = false
  }
}
</script>

<style scoped>
.back-home-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 13px;
  font-weight: 700;
  color: #ec2263;
}

.google-btn {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid #d8dde5;
  border-radius: 999px;
  background: #fff;
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.google-btn:hover:not(:disabled) {
  border-color: #c9d2df;
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}

.google-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.google-mark {
  display: inline-flex;
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(216, 221, 229, 0.9);
}

.google-mark svg {
  width: 18px;
  height: 18px;
}

.divider-row {
  position: relative;
  text-align: center;
}

.divider-row::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-top: 1px solid #e5e7eb;
}

.divider-row span {
  position: relative;
  display: inline-block;
  background: #fff;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.field-block {
  display: block;
}

.field-shell {
  position: relative;
  min-height: 72px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-shell-active {
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.field-floating-label {
  position: absolute;
  left: 18px;
  top: 24px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
  transition: transform 0.18s ease, top 0.18s ease, font-size 0.18s ease, color 0.18s ease;
}

.field-shell-active .field-floating-label {
  top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.field-control {
  width: 100%;
  min-height: 72px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  padding: 28px 18px 12px;
  font-size: 16px;
  color: #111827;
}

.field-control:focus {
  outline: none;
}

.single-action {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  background: rgba(156, 163, 175, 0.12);
  color: #6b7280;
}
</style>
